import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Zap, Mic2, Music2, Volume2, Sparkles, Loader2, Play, Pause, Download,
  ChevronLeft, ChevronRight, Check, X, ArrowRight, Trash2, RefreshCw,
  Upload, FileAudio, FileText, Clock,
} from "lucide-react";
import VoiceRecorderModal from "@/components/VoiceRecorderModal";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { appConfirm } from "@/components/AppDialogHost";

// ── Design tokens (from the mockup) ─────────────────────────────────
const T = {
  bg:       "#0f1117",
  card:     "#1a1e2a",
  elevated: "#161923",
  border:   "#232632",
  primary:  "#4a9eff",
  purple:   "#7c5cff",
  success:  "#3ddc97",
  warning:  "#ffb84a",
  text:     "#e7ebf6",
  muted:    "#7b8397",
  faint:    "#4a5060",
};

const MODES = [
  {
    id: "with_vocals",
    icon: Mic2,
    title: "With Vocals",
    subtitle: "A sung or spoken tagline over a music bed — station IDs, hooks, promo lines",
  },
  {
    id: "instrumental",
    icon: Music2,
    title: "Instrumental Only",
    subtitle: "Just a music bed or stinger — no vocal, ready to use as-is or under a live read",
  },
];

export default function QuickPromoCreator() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Wizard state
  const [step, setStep]           = useState(1);   // 1 mode | 2 bed | 3 vocal | 4 preview
  const [mode, setMode]           = useState("with_vocals");

  // Step 2 — bed
  const [beds, setBeds]           = useState([]);
  const [bedsLoading, setBedsLoading] = useState(false);
  const [selectedBedId, setSelectedBedId] = useState(null);

  // Step 3 — vocal
  const [voices, setVoices]       = useState([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState("onyx");
  const [promoName, setPromoName] = useState("");
  const [tagline, setTagline]     = useState("");
  const [voiceOver, setVoiceOver] = useState(false);   // instrumental mode: lay a VO over the full bed
  const [suggestions, setSuggestions] = useState([]);
  const [suggMood, setSuggMood]   = useState("smooth late-night");
  const [suggesting, setSuggesting] = useState(false);

  // Custom vocal upload (replaces TTS when set)
  const [customVocal, setCustomVocal] = useState(null); // { asset_id, label, source }
  const [uploadingVocal, setUploadingVocal] = useState(false);
  const vocalFileInputRef = useRef(null);
  const voFileInputRef = useRef(null);
  const [showRecorder, setShowRecorder] = useState(false);

  // Custom bed upload (adds to bed library + auto-selects)
  const [uploadingBed, setUploadingBed] = useState(false);
  const bedFileInputRef = useRef(null);

  // Global "now playing preview" — only one preview plays at a time
  // across voices + beds. Key format: `voice:{id}` | `bed:{asset_id}` | null.
  const [previewingKey, setPreviewingKey] = useState(null);
  const previewAudioRef = useRef(null);

  // ── Draft state (auto-save + resume) ─────────────────────────────
  // Same pattern as PodcastCreator (Update #165). draftId is server-
  // assigned on first save; all subsequent saves upsert into that
  // row via `POST /api/promo/drafts`.
  const [draftId, setDraftId] = useState(null);
  const [drafts, setDrafts]   = useState([]);   // latest 5 on landing
  const [draftsLoading, setDraftsLoading] = useState(true);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [draftBusy, setDraftBusy] = useState(false);
  const saveTimerRef = useRef(null);

  // Step 4 — preview + render
  const [rendering, setRendering] = useState(false);
  const [renderedPromo, setRenderedPromo] = useState(null);   // full promo doc
  const [audioUrl, setAudioUrl]           = useState(null);   // blob URL for <audio>

  // Bed generation state
  const [genOpen, setGenOpen]     = useState(false);
  const [genPrompt, setGenPrompt] = useState("");
  const [genGenre, setGenGenre]   = useState("Pop");
  const [genMood, setGenMood]     = useState("uplifting");
  const [genDuration, setGenDuration] = useState(15);
  const [generating, setGenerating] = useState(false);

  // Mix levels (locked to sensible defaults per mockup)
  const [vocalGain, setVocalGain] = useState(1.0);
  const [bedGain, setBedGain]     = useState(0.35);

  // ── Load voices + beds on mount ─────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const [v, b] = await Promise.all([
          api.get("/promo/voices"),
          api.get("/promo/beds?limit=30"),
        ]);
        setVoices(v.data.voices || []);
        setSelectedVoiceId(v.data.default_voice_id || "onyx");
        setBeds(b.data.items || []);
      } catch (e) {
        toast.error("Could not load promo tools");
      }
    })();
  }, []);

  const loadBeds = useCallback(async () => {
    setBedsLoading(true);
    try {
      const r = await api.get("/promo/beds?limit=30");
      setBeds(r.data.items || []);
    } finally {
      setBedsLoading(false);
    }
  }, []);

  // ── Audio preview (voices + beds share one "now playing" state) ──
  const stopPreview = useCallback(() => {
    if (previewAudioRef.current) {
      try {
        // Detach handlers BEFORE clearing src — otherwise setting src=""
        // fires onerror on the old element and shows a false error toast.
        previewAudioRef.current.onerror = null;
        previewAudioRef.current.onended = null;
        previewAudioRef.current.pause();
        previewAudioRef.current.src = "";
      } catch { /* no-op */ }
      previewAudioRef.current = null;
    }
    setPreviewingKey(null);
  }, []);

  useEffect(() => () => stopPreview(), [stopPreview]);

  const playPreview = useCallback(async (key, endpoint) => {
    // Toggle off if the same button is clicked twice
    if (previewingKey === key) {
      stopPreview();
      return;
    }
    stopPreview();
    setPreviewingKey(key);
    try {
      const r = await api.get(endpoint, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const audio = new Audio(url);
      audio.onended = () => {
        URL.revokeObjectURL(url);
        setPreviewingKey((k) => (k === key ? null : k));
      };
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        setPreviewingKey((k) => (k === key ? null : k));
        toast.error("Preview playback failed");
      };
      previewAudioRef.current = audio;
      await audio.play();
    } catch (e) {
      setPreviewingKey(null);
      const detail = e?.response?.data?.detail;
      // Blob-typed 402 responses need to be parsed to see the message
      if (e?.response?.status === 402) {
        toast.error("Preview requires an ElevenLabs key (upgrade voice)");
      } else if (detail) {
        toast.error(String(detail).slice(0, 120));
      } else {
        toast.error("Preview failed");
      }
    }
  }, [previewingKey, stopPreview]);

  // ── Custom bed upload ────────────────────────────────────────────
  const uploadCustomBed = useCallback(async (file) => {
    if (!file) return;
    const okTypes = ["audio/mpeg", "audio/wav", "audio/x-wav", "audio/mp4",
                     "audio/aac", "audio/flac", "audio/ogg", "audio/x-m4a"];
    if (file.size > 60 * 1024 * 1024) {
      toast.error("File exceeds 60 MB — please pick a shorter or lower-bitrate file");
      return;
    }
    if (file.type && !okTypes.includes(file.type) && !/\.(mp3|wav|m4a|aac|flac|ogg)$/i.test(file.name)) {
      toast.error("Unsupported format — use MP3 / WAV / M4A / FLAC / OGG");
      return;
    }
    setUploadingBed(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("kind", "bed");
      form.append("label", file.name.replace(/\.[^.]+$/, ""));
      form.append("source", "user_uploaded");
      form.append("normalize", "false"); // backend loudnorms at mix time
      const r = await api.post("/imaging/assets/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const asset = r.data;
      // Prepend into local bed list & auto-select
      setBeds((prev) => [{
        asset_id:     asset.asset_id,
        label:        asset.label,
        content_type: asset.content_type,
        duration_s:   asset.duration_seconds,
        genre:        asset.genre || "uploaded",
        source:       "user_uploaded",
        created_at:   asset.created_at,
      }, ...prev]);
      setSelectedBedId(asset.asset_id);
      toast.success("Bed uploaded — auto-loudness-normalized at mix time");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingBed(false);
      if (bedFileInputRef.current) bedFileInputRef.current.value = "";
    }
  }, []);

  // ── Custom vocal upload (replaces TTS) ───────────────────────────
  const uploadCustomVocal = useCallback(async (file) => {
    if (!file) return;
    if (file.size > 60 * 1024 * 1024) {
      toast.error("File exceeds 60 MB");
      return;
    }
    if (file.type && !/^audio\//.test(file.type) && !/\.(mp3|wav|m4a|aac|flac|ogg)$/i.test(file.name)) {
      toast.error("Unsupported format — use MP3 / WAV / M4A / FLAC / OGG");
      return;
    }
    setUploadingVocal(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("kind", "voice");
      form.append("label", file.name.replace(/\.[^.]+$/, ""));
      form.append("source", "user_uploaded");
      form.append("normalize", "false");
      const r = await api.post("/imaging/assets/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const asset = r.data;
      setCustomVocal({
        asset_id: asset.asset_id,
        label: asset.label,
        source: "user_uploaded",
        duration_s: asset.duration_seconds,
      });
      toast.success("Vocal uploaded — will replace AI TTS in the mix");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingVocal(false);
      if (vocalFileInputRef.current) vocalFileInputRef.current.value = "";
    }
  }, []);

  const clearCustomVocal = useCallback(() => {
    setCustomVocal(null);
    stopPreview();
  }, [stopPreview]);

  // ── Drafts: fetch latest 5 on mount ────────────────────────────
  const refreshDrafts = useCallback(async () => {
    try {
      const r = await api.get("/promo/drafts?limit=5");
      setDrafts(r.data.items || []);
    } catch {
      // Non-fatal — panel just stays empty
    } finally {
      setDraftsLoading(false);
    }
  }, []);

  useEffect(() => { refreshDrafts(); }, [refreshDrafts]);

  // ── Draft title derivation ─────────────────────────────────────
  // Keeps the Resume panel readable at every wizard stage.
  const derivedDraftTitle = useCallback(() => {
    if (promoName.trim()) return promoName.trim().slice(0, 200);
    if (mode === "with_vocals" && tagline.trim()) return tagline.trim().slice(0, 90);
    if (mode === "instrumental" && selectedBedId) {
      const b = beds.find((x) => x.asset_id === selectedBedId);
      if (b?.label) return `${b.label} (instrumental)`;
    }
    return "Untitled promo draft";
  }, [promoName, mode, tagline, selectedBedId, beds]);

  // ── Auto-save (debounced 1.2s) ────────────────────────────────
  // Fires once the user has entered SOMETHING meaningful — a name,
  // a bed selection, a tagline, or advanced past step 1. Prevents
  // saving empty drafts on the mode picker.
  const hasContentToSave = useMemo(() => {
    if (step > 1) return true;
    return !!(promoName.trim() || tagline.trim() || selectedBedId || customVocal);
  }, [step, promoName, tagline, selectedBedId, customVocal]);

  useEffect(() => {
    if (!hasContentToSave) return;
    if (renderedPromo) return; // Don't save drafts of already-rendered promos
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      if (draftBusy) return;
      setDraftBusy(true);
      try {
        const r = await api.post("/promo/drafts", {
          draft_id: draftId,
          title:    derivedDraftTitle(),
          mode,
          metadata: {
            step,
            promoName, tagline,
            selectedBedId,
            selectedVoiceId,
            customVocal,   // {asset_id, label, source, duration_s} or null
            vocalGain, bedGain,
            suggMood,
          },
        });
        if (r.data.draft_id && r.data.draft_id !== draftId) {
          setDraftId(r.data.draft_id);
        }
        setDraftSavedAt(Date.now());
      } catch {
        // Non-fatal
      } finally {
        setDraftBusy(false);
      }
    }, 1200);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [
    hasContentToSave, draftId, mode, step,
    promoName, tagline, selectedBedId, selectedVoiceId, customVocal,
    vocalGain, bedGain, suggMood, renderedPromo, draftBusy, derivedDraftTitle,
  ]);

  // ── Resume a draft ─────────────────────────────────────────────
  const resumeDraft = useCallback((d) => {
    stopPreview();
    const md = d.metadata || {};
    setDraftId(d.draft_id);
    setMode(d.mode || "with_vocals");
    setPromoName(md.promoName || "");
    setTagline(md.tagline || "");
    setSelectedBedId(md.selectedBedId || null);
    if (md.selectedVoiceId) setSelectedVoiceId(md.selectedVoiceId);
    if (typeof md.vocalGain === "number") setVocalGain(md.vocalGain);
    if (typeof md.bedGain === "number") setBedGain(md.bedGain);
    if (md.suggMood) setSuggMood(md.suggMood);
    setCustomVocal(md.customVocal || null);
    // Clamp step 1..4 — no point resuming into Preview since rendered
    // audio isn't stored in drafts.
    const target = Math.max(1, Math.min(4, md.step || 1));
    setStep(target);
    toast.success(`Resumed "${(d.title || 'Untitled').slice(0, 40)}"`);
  }, [stopPreview]);

  // ── Delete a draft ─────────────────────────────────────────────
  const deleteDraft = useCallback(async (targetDraftId, e) => {
    if (e) e.stopPropagation();
    if (!targetDraftId) return;
    if (!await appConfirm("Delete this draft? This cannot be undone.")) return;
    try {
      await api.delete(`/promo/drafts/${targetDraftId}`);
      setDrafts((prev) => prev.filter((d) => d.draft_id !== targetDraftId));
      if (targetDraftId === draftId) {
        setDraftId(null);
      }
      toast.success("Draft deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  }, [draftId]);


  // ── Tagline AI suggest ───────────────────────────────────────────
  const suggestTaglines = useCallback(async () => {
    if (!user) return;
    setSuggesting(true);
    try {
      const stationName = user.station_name || promoName || "Your Station";
      const r = await api.post("/promo/tagline-suggest", {
        station_name: stationName,
        mode: (mode === "instrumental" && voiceOver) ? "with_vocals" : mode,
        promo_name: promoName || null,
        mood: suggMood,
        count: 3,
      });
      setSuggestions(r.data.suggestions || []);
    } catch (e) {
      toast.error("Tagline suggestion failed");
    } finally {
      setSuggesting(false);
    }
  }, [user, promoName, mode, voiceOver, suggMood]);

  // ── Bed generation (Mubert) ──────────────────────────────────────
  const generateBed = useCallback(async () => {
    setGenerating(true);
    try {
      const r = await api.post("/imaging/generate/mubert", {
        genre:       genGenre,
        mood:        genMood,
        intensity:   "medium",
        duration:    genDuration,
        description: genPrompt,
        piece_type:  "sweeper",
      });
      // Refresh the bed list to include the new one
      await loadBeds();
      // Auto-select the freshly generated bed
      if (r.data?.asset_id) setSelectedBedId(r.data.asset_id);
      toast.success("Bed generated");
      setGenOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bed generation failed");
    } finally {
      setGenerating(false);
    }
  }, [genGenre, genMood, genDuration, genPrompt, loadBeds]);

  const [seqSending, setSeqSending] = useState(false);
  const [seqSent, setSeqSent] = useState(false);
  const sendToSequencer = useCallback(async () => {
    if (!renderedPromo?.promo_id) return;
    setSeqSending(true);
    try {
      const r = await api.post(`/promo/${renderedPromo.promo_id}/send-to-sequencer`);
      setSeqSent(true);
      toast.success(r.data.already_added
        ? "Already in your Imaging library"
        : "Added to your Imaging library — open Imaging Studio → Sequencer and drag it onto the timeline");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send to Sequencer");
    } finally {
      setSeqSending(false);
    }
  }, [renderedPromo]);

  // ── Render the promo ─────────────────────────────────────────────
  const renderPromo = useCallback(async () => {
    const instVoiceOver = mode === "instrumental" && voiceOver;
    if (!promoName.trim()) {
      toast.error("Give your promo a name");
      return;
    }
    if (mode === "with_vocals" && !tagline.trim() && !customVocal) {
      toast.error("Enter a tagline (or upload your own vocal)");
      return;
    }
    if (mode === "instrumental" && !selectedBedId) {
      toast.error("Pick a bed for instrumental mode");
      return;
    }
    if (instVoiceOver && !tagline.trim() && !customVocal) {
      toast.error("Write the voice-over line, upload your own recording, or switch the voice-over off");
      return;
    }
    stopPreview();
    setRendering(true);
    setRenderedPromo(null);
    setSeqSent(false);
    setAudioUrl(null);
    try {
      const wantsVocal = mode === "with_vocals" || instVoiceOver;
      const r = await api.post("/promo", {
        name:          promoName.trim(),
        mode:          wantsVocal ? "with_vocals" : mode,
        tagline_text:  wantsVocal ? (tagline.trim() || (customVocal ? customVocal.label : "")) : null,
        voice_id:      wantsVocal && !customVocal ? selectedVoiceId : null,
        custom_vocal_asset_id: wantsVocal && customVocal ? customVocal.asset_id : null,
        bed_asset_id:  selectedBedId,
        vocal_gain:    vocalGain,
        bed_gain:      bedGain,
        keep_full_bed: instVoiceOver,
      });
      const promo = r.data;
      setRenderedPromo(promo);
      // Stream the audio via authenticated fetch → objectURL
      const audio = await api.get(`/promo/${promo.promo_id}/audio`, { responseType: "blob" });
      const url = URL.createObjectURL(audio.data);
      setAudioUrl(url);
      // Promo is now rendered → this draft is no longer "in progress".
      // Best-effort delete + refresh the Resume panel so it stays clean.
      if (draftId) {
        try {
          await api.delete(`/promo/drafts/${draftId}`);
          setDraftId(null);
        } catch { /* non-fatal */ }
      }
      refreshDrafts();
      toast.success("Promo rendered — preview below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Render failed");
    } finally {
      setRendering(false);
    }
  }, [promoName, mode, voiceOver, tagline, selectedVoiceId, selectedBedId, vocalGain, bedGain, customVocal, stopPreview, draftId, refreshDrafts]);

  const downloadExport = useCallback(async () => {
    if (!renderedPromo) return;
    try {
      const r = await api.get(`/promo/${renderedPromo.promo_id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const safe = renderedPromo.name.replace(/[^A-Za-z0-9_\- ]+/g, "_");
      a.href = url;
      a.download = `${safe}_promo.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded promo + manifest");
    } catch {
      toast.error("Export download failed");
    }
  }, [renderedPromo]);

  const startOver = useCallback(() => {
    stopPreview();
    setStep(1);
    setPromoName("");
    setTagline("");
    setSelectedBedId(null);
    setSuggestions([]);
    setRenderedPromo(null);
    setSeqSent(false);
    setAudioUrl(null);
    setCustomVocal(null);
    // A fresh session gets a fresh draft row.
    setDraftId(null);
    setDraftSavedAt(null);
    refreshDrafts();
  }, [stopPreview, refreshDrafts]);

  // Which steps are enabled for the current mode
  const stepsForMode = useMemo(
    () => (mode === "instrumental"
      ? [{ n: 1, label: "Pick a bed" }, { n: 2, label: "Preview & create" }]
      : [{ n: 1, label: "Pick a bed" }, { n: 2, label: "Add vocal" }, { n: 3, label: "Preview & create" }]),
    [mode]
  );

  // Effective step (indexed among stepsForMode)
  const activeStep = mode === "instrumental" && step === 3 ? 2 : step;

  const bedSelected = useMemo(
    () => beds.find((b) => b.asset_id === selectedBedId) || null,
    [beds, selectedBedId]
  );

  return (
    <div className="min-h-full" style={{ background: T.bg, color: T.text }}>
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-8 md:py-12" data-testid="quick-promo-creator">
        {/* Header */}
        <div className="flex items-center gap-3 mb-3">
          <div className="rounded-lg p-2" style={{ background: `${T.purple}22`, border: `1px solid ${T.purple}55` }}>
            <Zap size={18} style={{ color: T.purple }} />
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: -0.5 }}>
            Quick Promo Creator
          </h1>
          {/* Auto-save chip — same pattern as PodcastCreator. Only
              shown once we've persisted at least once and the user
              isn't looking at a rendered promo. */}
          {draftSavedAt && !renderedPromo && (
            <span className="rounded-full inline-flex items-center gap-1 px-2 py-0.5 ml-auto"
                  data-testid="promo-draft-saved-chip"
                  style={{ background: `${T.success}1a`, border: `1px solid ${T.success}55`,
                           color: T.success, fontSize: 10, fontWeight: 500 }}>
              {draftBusy
                ? <Loader2 size={10} className="animate-spin" />
                : <Check size={10} />}
              {draftBusy ? "Saving…" : `Draft saved · ${formatAgo(new Date(draftSavedAt).toISOString())}`}
            </span>
          )}
        </div>
        <p className="mb-8 max-w-2xl" style={{ color: T.muted, fontSize: 14, lineHeight: 1.5 }}>
          Build a sweeper, jingle, or promo tag in one fast flow — picks sensible defaults so you&apos;re not manually mixing in Production Suite for a 10-second drop.
        </p>

        {/* ── Resume drafts panel ─ */}
        {/* Surfaces the user's latest 5 in-progress promos so they
            can pick up where they left off. Hidden on the fresh-empty
            state and while initial fetch is still loading. Placed
            above the mode picker so it's the FIRST thing the user
            sees on landing — matches PodcastCreator's UX. */}
        {!draftsLoading && drafts.length > 0 && !renderedPromo && (
          <div className="rounded-xl p-4 mb-6"
               style={{ background: T.card, border: `1px solid ${T.border}` }}
               data-testid="promo-drafts-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="rounded-lg p-1.5"
                     style={{ background: `${T.warning}22`, border: `1px solid ${T.warning}55` }}>
                  <FileText size={13} style={{ color: T.warning }} />
                </div>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: T.text }}>
                  Resume a draft
                </h3>
                <span style={{ fontSize: 11, color: T.muted }}>
                  · {drafts.length} in progress
                </span>
              </div>
              <button
                type="button"
                data-testid="promo-drafts-refresh"
                onClick={refreshDrafts}
                className="rounded-full inline-flex items-center gap-1 px-2 py-1"
                style={{ background: T.elevated, border: `1px solid ${T.border}`,
                         color: T.muted, fontSize: 10 }}
                title="Refresh"
              >
                <RefreshCw size={10} />
              </button>
            </div>

            <div className="space-y-1.5">
              {drafts.map((d) => (
                <PromoDraftRow
                  key={d.draft_id}
                  draft={d}
                  onResume={() => resumeDraft(d)}
                  onDelete={(e) => deleteDraft(d.draft_id, e)}
                />
              ))}
            </div>
          </div>
        )}

        {/* ── STEP 0: Mode picker (always visible at top) ────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {MODES.map((m) => {
            const active = mode === m.id;
            const Icon = m.icon;
            return (
              <button
                key={m.id}
                type="button"
                data-testid={`promo-mode-${m.id}`}
                onClick={() => {
                  setMode(m.id);
                  setStep(1);
                  setTagline("");
                  setSuggestions([]);
                }}
                className="text-left rounded-xl p-5 transition"
                style={{
                  background: active
                    ? `linear-gradient(180deg, ${T.purple}1a, ${T.card} 60%)`
                    : T.card,
                  border: active ? `1px solid ${T.purple}` : `1px solid ${T.border}`,
                  boxShadow: active ? `0 0 0 3px ${T.purple}22` : "none",
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={16} style={{ color: active ? T.purple : T.muted }} />
                  <span style={{ fontSize: 15, fontWeight: 600 }}>{m.title}</span>
                  {active && <Check size={14} style={{ color: T.purple, marginLeft: "auto" }} />}
                </div>
                <div style={{ color: T.muted, fontSize: 13, lineHeight: 1.5 }}>{m.subtitle}</div>
              </button>
            );
          })}
        </div>

        {/* ── Step tracker ──────────────────────────────────────── */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          {stepsForMode.map((s, i) => {
            const done = activeStep > s.n;
            const active = activeStep === s.n;
            return (
              <React.Fragment key={s.n}>
                <div className="flex items-center gap-2">
                  <span
                    className="inline-flex items-center justify-center rounded-full"
                    style={{
                      width: 22, height: 22,
                      background: done ? T.success : active ? T.purple : T.elevated,
                      color: done || active ? "#000" : T.muted,
                      fontSize: 11, fontWeight: 700,
                      border: `1px solid ${done ? T.success : active ? T.purple : T.border}`,
                    }}
                  >
                    {done ? <Check size={11} /> : s.n}
                  </span>
                  <span style={{ fontSize: 12, color: active ? T.text : T.muted, fontWeight: active ? 600 : 500 }}>
                    {s.label}
                  </span>
                </div>
                {i < stepsForMode.length - 1 && (
                  <ChevronRight size={12} style={{ color: T.faint }} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* ── STEP 1: Bed selection ─────────────────────────────── */}
        {step === 1 && (
          <StepCard title="Pick a music bed" subtitle="Choose from your library, upload your own, or generate a new one via Mubert.">
            <input
              ref={bedFileInputRef}
              type="file"
              accept="audio/*,.mp3,.wav,.m4a,.aac,.flac,.ogg"
              onChange={(e) => uploadCustomBed(e.target.files?.[0])}
              className="hidden"
              data-testid="promo-bed-file-input"
            />
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div style={{ color: T.muted, fontSize: 12 }}>{beds.length} bed{beds.length === 1 ? "" : "s"} in library</div>
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  data-testid="promo-refresh-beds"
                  onClick={loadBeds}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
                  style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.muted, fontSize: 12 }}
                >
                  <RefreshCw size={12} className={bedsLoading ? "animate-spin" : ""} />
                  Refresh
                </button>
                <button
                  type="button"
                  data-testid="promo-upload-bed"
                  onClick={() => bedFileInputRef.current?.click()}
                  disabled={uploadingBed}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
                  style={{
                    background: T.elevated,
                    border: `1px solid ${T.primary}`,
                    color: T.primary, fontSize: 12, fontWeight: 500,
                    opacity: uploadingBed ? 0.6 : 1,
                  }}
                >
                  {uploadingBed ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                  {uploadingBed ? "Uploading…" : "Upload your own"}
                </button>
                <button
                  type="button"
                  data-testid="promo-open-generate-bed"
                  onClick={() => setGenOpen((v) => !v)}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
                  style={{
                    background: `linear-gradient(135deg,${T.primary} 0%,${T.purple} 100%)`,
                    color: "#fff", fontSize: 12, fontWeight: 500,
                  }}
                >
                  <Sparkles size={12} />
                  Generate new bed
                </button>
              </div>
            </div>

            {genOpen && (
              <div className="mb-4 rounded-lg p-4"
                   style={{ background: T.elevated, border: `1px solid ${T.border}` }}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                  <TextInput label="Genre" value={genGenre} onChange={setGenGenre} testid="promo-bed-genre" />
                  <TextInput label="Mood" value={genMood} onChange={setGenMood} testid="promo-bed-mood" />
                  <NumberInput label="Duration (s)" value={genDuration} min={5} max={60} onChange={setGenDuration} testid="promo-bed-duration" />
                  <TextInput label="Vibe hint" value={genPrompt} onChange={setGenPrompt} placeholder="warm sunset drive" testid="promo-bed-prompt" />
                </div>
                <button
                  type="button"
                  data-testid="promo-generate-bed-submit"
                  onClick={generateBed}
                  disabled={generating}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5"
                  style={{
                    background: `linear-gradient(135deg,${T.primary} 0%,${T.purple} 100%)`,
                    color: "#fff", fontSize: 12, fontWeight: 500,
                    opacity: generating ? 0.6 : 1,
                  }}
                >
                  {generating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  {generating ? "Generating (30-60s)…" : "Generate bed via Mubert"}
                </button>
              </div>
            )}

            {beds.length === 0 ? (
              <EmptyState icon={Music2} title="No beds in your library yet"
                          subtitle="Click Upload your own or Generate new bed above." />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {beds.slice(0, 12).map((b) => {
                  const active = selectedBedId === b.asset_id;
                  const key = `bed:${b.asset_id}`;
                  const isPlaying = previewingKey === key;
                  return (
                    <div
                      key={b.asset_id}
                      className="flex items-center gap-2 rounded-lg px-3 py-2.5"
                      style={{
                        background: active ? `${T.primary}1a` : T.elevated,
                        border: `1px solid ${active ? T.primary : T.border}`,
                      }}
                    >
                      <button
                        type="button"
                        data-testid={`promo-bed-preview-${b.asset_id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          playPreview(key, `/imaging/assets/${b.asset_id}/audio`);
                        }}
                        className="rounded-full inline-flex items-center justify-center flex-shrink-0"
                        title={isPlaying ? "Stop preview" : "Preview bed"}
                        style={{
                          width: 30, height: 30,
                          background: isPlaying ? T.warning : (active ? T.primary : T.faint),
                          color: isPlaying || active ? "#000" : T.text,
                          border: `1px solid ${isPlaying ? T.warning : (active ? T.primary : T.border)}`,
                        }}
                      >
                        {isPlaying ? <Pause size={12} /> : <Play size={12} style={{ marginLeft: 2 }} />}
                      </button>
                      <button
                        type="button"
                        data-testid={`promo-bed-${b.asset_id}`}
                        onClick={() => setSelectedBedId(active ? null : b.asset_id)}
                        className="flex items-center gap-3 flex-1 min-w-0 text-left"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span style={{ fontSize: 13, fontWeight: 500, color: T.text }} className="truncate">
                              {b.label || b.asset_id}
                            </span>
                            {b.source === "user_uploaded" && (
                              <span className="rounded px-1 py-0.5"
                                    style={{ background: `${T.primary}33`, color: T.primary, fontSize: 8, fontWeight: 700 }}>
                                YOURS
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 11, color: T.muted }}>
                            {(b.genre || "bed")} · {b.duration_s ? `${Math.round(b.duration_s)}s` : "music"}
                          </div>
                        </div>
                        {active && <Check size={16} style={{ color: T.primary }} />}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <StepFooter
              onBack={null}
              onNext={() => setStep(mode === "instrumental" ? 4 : 2)}
              nextLabel={mode === "instrumental" ? "Next: Preview →" : "Next: Add vocal →"}
              nextDisabled={mode === "instrumental" && !selectedBedId}
              testid="promo-step1-next"
            />
          </StepCard>
        )}

        {/* ── STEP 2: Vocal (with_vocals only) ─────────────────── */}
        {step === 2 && mode === "with_vocals" && (
          <StepCard title="Add vocal" subtitle="Give the promo a name, write or AI-generate the tagline, pick a voice.">
            <div className="mb-4">
              <FieldLabel>Promo Name</FieldLabel>
              <input
                type="text"
                data-testid="promo-name-input"
                placeholder="e.g., Late Night Sweeper #1"
                value={promoName}
                onChange={(e) => setPromoName(e.target.value)}
                className="w-full rounded-lg px-3 py-2"
                style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.text, fontSize: 13 }}
              />
            </div>

            <div className="mb-4">
              <FieldLabel>Tagline</FieldLabel>
              <textarea
                data-testid="promo-tagline-input"
                placeholder="Seductive Vibes Radio — the sound of the night"
                value={tagline}
                onChange={(e) => setTagline(e.target.value.slice(0, 240))}
                rows={2}
                className="w-full rounded-lg px-3 py-2"
                style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.text, fontSize: 13, resize: "vertical" }}
              />
              <div className="flex items-center justify-between mt-1">
                <span style={{ fontSize: 10, color: T.faint }}>{tagline.length} / 240</span>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    data-testid="promo-tagline-mood"
                    value={suggMood}
                    onChange={(e) => setSuggMood(e.target.value)}
                    placeholder="mood (e.g., smooth late-night)"
                    className="rounded px-2 py-1"
                    style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.text, fontSize: 11, width: 220 }}
                  />
                  <button
                    type="button"
                    data-testid="promo-tagline-suggest"
                    onClick={suggestTaglines}
                    disabled={suggesting}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1"
                    style={{
                      background: `linear-gradient(135deg,${T.primary} 0%,${T.purple} 100%)`,
                      color: "#fff", fontSize: 11, fontWeight: 500,
                      opacity: suggesting ? 0.6 : 1,
                    }}
                  >
                    {suggesting ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
                    Suggest →
                  </button>
                </div>
              </div>
            </div>

            {suggestions.length > 0 && (
              <div className="mb-5 rounded-lg p-3" style={{ background: T.elevated, border: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 10, color: T.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                  AI suggestions
                </div>
                <div className="space-y-2">
                  {suggestions.map((s, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 rounded px-2 py-1.5"
                         style={{ background: T.card, border: `1px solid ${T.border}` }}>
                      <span style={{ fontSize: 13, color: T.text }} className="flex-1">{s}</span>
                      <button
                        type="button"
                        data-testid={`promo-tagline-use-${i}`}
                        onClick={() => setTagline(s)}
                        className="rounded px-2 py-1"
                        style={{ background: T.primary, color: "#000", fontSize: 10, fontWeight: 600 }}
                      >
                        Use this
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <FieldLabel>Choose a voice</FieldLabel>
            {customVocal && (
              <div className="mb-3 rounded-lg p-3 flex items-center gap-3"
                   style={{ background: `${T.primary}11`, border: `1px solid ${T.primary}55` }}
                   data-testid="promo-custom-vocal-chip">
                <FileAudio size={16} style={{ color: T.primary }} />
                <div className="flex-1 min-w-0">
                  <div style={{ fontSize: 12, color: T.text, fontWeight: 600 }} className="truncate">
                    Using your uploaded vocal
                  </div>
                  <div style={{ fontSize: 11, color: T.muted }} className="truncate">
                    {customVocal.label}
                    {customVocal.duration_s ? ` · ${Math.round(customVocal.duration_s)}s` : ""}
                    {" · loudness-normalized at mix time"}
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="promo-custom-vocal-preview"
                  onClick={() => playPreview(
                    `bed:${customVocal.asset_id}`,
                    `/imaging/assets/${customVocal.asset_id}/audio`,
                  )}
                  className="rounded-full inline-flex items-center justify-center"
                  title="Preview uploaded vocal"
                  style={{
                    width: 28, height: 28,
                    background: previewingKey === `bed:${customVocal.asset_id}` ? T.warning : T.faint,
                    color: previewingKey === `bed:${customVocal.asset_id}` ? "#000" : T.text,
                  }}
                >
                  {previewingKey === `bed:${customVocal.asset_id}`
                    ? <Pause size={12} />
                    : <Play size={12} style={{ marginLeft: 2 }} />}
                </button>
                <button
                  type="button"
                  data-testid="promo-custom-vocal-clear"
                  onClick={clearCustomVocal}
                  className="rounded-full inline-flex items-center justify-center"
                  title="Clear custom vocal (use AI voice instead)"
                  style={{
                    width: 28, height: 28,
                    background: T.elevated,
                    border: `1px solid ${T.border}`, color: T.muted,
                  }}
                >
                  <X size={12} />
                </button>
              </div>
            )}

            <input
              ref={vocalFileInputRef}
              type="file"
              accept="audio/*,.mp3,.wav,.m4a,.aac,.flac,.ogg"
              onChange={(e) => uploadCustomVocal(e.target.files?.[0])}
              className="hidden"
              data-testid="promo-vocal-file-input"
            />
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <span style={{ fontSize: 11, color: T.faint }}>
                {customVocal
                  ? "AI voices disabled — your uploaded vocal will be used"
                  : "Pick an AI voice below, or upload your own pre-recorded vocal"}
              </span>
              <button
                type="button"
                data-testid="promo-upload-vocal"
                onClick={() => vocalFileInputRef.current?.click()}
                disabled={uploadingVocal}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1"
                style={{
                  background: T.elevated,
                  border: `1px solid ${T.primary}`,
                  color: T.primary, fontSize: 11, fontWeight: 500,
                  opacity: uploadingVocal ? 0.6 : 1,
                }}
              >
                {uploadingVocal ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                {uploadingVocal ? "Uploading…" : "Upload your own vocal"}
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3"
                 style={{ opacity: customVocal ? 0.35 : 1, pointerEvents: customVocal ? "none" : "auto" }}>
              {voices.map((v) => {
                const active = selectedVoiceId === v.id;
                const key = `voice:${v.id}`;
                const isPlaying = previewingKey === key;
                return (
                  <div
                    key={v.id}
                    className="flex items-center gap-2 rounded-lg px-3 py-2 transition relative"
                    style={{
                      background: active ? `${T.purple}1a` : T.elevated,
                      border: `1px solid ${active ? T.purple : T.border}`,
                      opacity: v.needs_key ? 0.55 : 1,
                    }}
                  >
                    <button
                      type="button"
                      data-testid={`promo-voice-preview-${v.id}`}
                      disabled={v.needs_key}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (v.needs_key) return;
                        playPreview(key, `/promo/voice-preview/${v.id}`);
                      }}
                      className="rounded-full inline-flex items-center justify-center flex-shrink-0"
                      title={isPlaying ? "Stop preview" : "Preview voice"}
                      style={{
                        width: 26, height: 26,
                        background: isPlaying ? T.warning : (active ? T.purple : T.faint),
                        color: isPlaying || active ? "#000" : T.text,
                        border: `1px solid ${isPlaying ? T.warning : (active ? T.purple : T.border)}`,
                        cursor: v.needs_key ? "not-allowed" : "pointer",
                      }}
                    >
                      {isPlaying ? <Pause size={10} /> : <Play size={10} style={{ marginLeft: 1 }} />}
                    </button>
                    <button
                      type="button"
                      data-testid={`promo-voice-${v.id}`}
                      onClick={() => !v.needs_key && setSelectedVoiceId(v.id)}
                      disabled={v.needs_key}
                      className="flex-1 min-w-0 text-left"
                      style={{ cursor: v.needs_key ? "not-allowed" : "pointer" }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 500, color: T.text }} className="truncate">
                        {v.label.split(" — ")[0]}
                      </div>
                      <div style={{ fontSize: 10, color: T.muted }} className="truncate">
                        {v.label.split(" — ")[1] || v.engine}
                      </div>
                    </button>
                    {v.premium && (
                      <span className="rounded px-1.5 py-0.5" style={{ background: T.warning, color: "#000", fontSize: 9, fontWeight: 700 }}>
                        UPGRADE
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            <StepFooter
              onBack={() => setStep(1)}
              onNext={() => setStep(4)}
              nextLabel="Next: Preview →"
              nextDisabled={!promoName.trim() || (!customVocal && !tagline.trim()) || (!customVocal && !selectedVoiceId)}
              testid="promo-step2-next"
            />
          </StepCard>
        )}

        {/* ── STEP 4: Preview & create ──────────────────────────── */}
        {step === 4 && (
          <StepCard title="Preview & create" subtitle="Auto-balanced mix — click Render to bake the final MP3.">
            {/* Summary */}
            <div className="rounded-lg p-3 mb-4"
                 style={{ background: T.elevated, border: `1px solid ${T.border}` }}>
              <SummaryRow label="Mode" value={mode === "with_vocals" ? "With Vocals" : (voiceOver ? "Instrumental + Voice-over" : "Instrumental Only")} />
              {mode === "with_vocals" && (
                <>
                  <SummaryRow label="Name"    value={promoName || "—"} />
                  <SummaryRow label="Tagline" value={tagline || (customVocal ? "(from your uploaded vocal)" : "—")} />
                  <SummaryRow
                    label="Voice"
                    value={customVocal
                      ? `Your upload · ${customVocal.label}`
                      : (voices.find((v) => v.id === selectedVoiceId) || {}).label || "—"}
                  />
                </>
              )}
              {mode === "instrumental" && (
                <>
                  <SummaryRow label="Name" value={promoName || "Untitled promo"} />
                  {voiceOver && (
                    <>
                      <SummaryRow label="Voice-over" value={tagline || (customVocal ? "(from your recording)" : "—")} />
                      <SummaryRow
                        label="Voice"
                        value={customVocal
                          ? `Your upload · ${customVocal.label}`
                          : (voices.find((v) => v.id === selectedVoiceId) || {}).label || "—"}
                      />
                    </>
                  )}
                </>
              )}
              <SummaryRow label="Bed" value={bedSelected ? bedSelected.label : (mode === "instrumental" ? "REQUIRED" : "None (dry vocal)")} />
              {(bedSelected?.source === "user_uploaded" || (customVocal && mode === "with_vocals")) && (
                <SummaryRow
                  label="Auto-norm"
                  value={[
                    bedSelected?.source === "user_uploaded" ? "bed" : null,
                    customVocal ? "vocal" : null,
                  ].filter(Boolean).join(" + ") + " (loudnorm -16 LUFS)"}
                />
              )}
            </div>

            {/* Mix preview visualization */}
            <div className="rounded-lg p-3 mb-4" style={{ background: T.elevated, border: `1px solid ${T.border}` }}>
              <div className="flex items-center justify-between mb-2">
                <span style={{ fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>Mix preview</span>
                <span style={{ fontSize: 10, color: T.success }}>Auto-balanced</span>
              </div>
              <MixBar label="Vocal" value={vocalGain} color={T.primary} onChange={setVocalGain} testid="promo-vocal-gain" disabled={mode === "instrumental" && !voiceOver} />
              <MixBar label="Bed"   value={bedGain}   color={T.purple}  onChange={setBedGain}   testid="promo-bed-gain"   disabled={!selectedBedId} />
            </div>

            {/* Instrumental mode — optional voice-over on top of the full bed */}
            {mode === "instrumental" && (
              <div className="rounded-lg p-3 mb-4" style={{ background: T.elevated, border: `1px solid ${voiceOver ? T.purple : T.border}` }}>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>Add a voice-over</div>
                    <div style={{ fontSize: 10.5, color: T.muted }}>
                      Station ID, jingle read or promo line — spoken over the <strong>full</strong> bed (the music keeps playing).
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid="promo-voiceover-toggle"
                    onClick={() => setVoiceOver((v) => !v)}
                    aria-pressed={voiceOver}
                    className="relative flex-shrink-0 rounded-full transition"
                    style={{ width: 42, height: 22, background: voiceOver ? T.purple : T.faint }}
                  >
                    <span className="absolute top-0.5 rounded-full transition-all"
                      style={{ width: 18, height: 18, left: voiceOver ? 22 : 2, background: "#fff" }} />
                  </button>
                </div>

                {voiceOver && (
                  <div className="mt-3" data-testid="promo-voiceover-panel">
                    {customVocal ? (
                      <div className="flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 mb-1"
                           style={{ background: T.card, border: `1px solid ${T.purple}66` }}
                           data-testid="promo-voiceover-custom-chip">
                        <div className="flex items-center gap-2 min-w-0">
                          <Mic2 size={13} style={{ color: T.purple, flexShrink: 0 }} />
                          <span className="truncate" style={{ fontSize: 12, color: T.text }}>
                            Your recording · {customVocal.label}
                          </span>
                        </div>
                        <button type="button" data-testid="promo-voiceover-custom-remove"
                          onClick={() => setCustomVocal(null)}
                          className="rounded px-2 py-1 flex-shrink-0"
                          style={{ background: T.faint, color: T.muted, fontSize: 10.5 }}>
                          Remove
                        </button>
                      </div>
                    ) : (
                      <>
                    <FieldLabel>Voice-over line</FieldLabel>
                    <textarea
                      data-testid="promo-voiceover-text"
                      placeholder="You're locked in to Seductive Vibes Radio — smooth R&B, all night long"
                      value={tagline}
                      onChange={(e) => setTagline(e.target.value.slice(0, 240))}
                      rows={2}
                      className="w-full rounded-lg px-3 py-2"
                      style={{ background: T.card, border: `1px solid ${T.border}`, color: T.text, fontSize: 13, resize: "vertical" }}
                    />
                    <div className="flex items-center justify-between mt-1 mb-3">
                      <span style={{ fontSize: 10, color: T.faint }}>{tagline.length} / 240</span>
                      <button
                        type="button"
                        data-testid="promo-voiceover-suggest"
                        onClick={suggestTaglines}
                        disabled={suggesting}
                        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1"
                        style={{
                          background: `linear-gradient(135deg,${T.primary} 0%,${T.purple} 100%)`,
                          color: "#fff", fontSize: 11, fontWeight: 500,
                          opacity: suggesting ? 0.6 : 1,
                        }}
                      >
                        {suggesting ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
                        Suggest →
                      </button>
                    </div>
                    {suggestions.length > 0 && (
                      <div className="mb-3 space-y-1.5">
                        {suggestions.map((s, i) => (
                          <div key={i} className="flex items-center justify-between gap-2 rounded px-2 py-1.5"
                               style={{ background: T.card, border: `1px solid ${T.border}` }}>
                            <span style={{ fontSize: 12, color: T.text }} className="flex-1">{s}</span>
                            <button type="button" data-testid={`promo-voiceover-use-${i}`}
                              onClick={() => setTagline(s)}
                              className="rounded px-2 py-1"
                              style={{ background: T.primary, color: "#000", fontSize: 10, fontWeight: 600 }}>
                              Use this
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <FieldLabel>Voice</FieldLabel>
                    <div className="flex items-center gap-2">
                      <select
                        data-testid="promo-voiceover-voice"
                        value={selectedVoiceId}
                        onChange={(e) => setSelectedVoiceId(e.target.value)}
                        className="flex-1 rounded-lg px-3 py-2"
                        style={{ background: T.card, border: `1px solid ${T.border}`, color: T.text, fontSize: 13 }}
                      >
                        {voices.filter((v) => !v.needs_key).map((v) => (
                          <option key={v.id} value={v.id}>{v.label}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        data-testid="promo-voiceover-voice-preview"
                        onClick={() => playPreview(`voice:${selectedVoiceId}`, `/promo/voice-preview/${selectedVoiceId}`)}
                        className="rounded-full inline-flex items-center justify-center flex-shrink-0"
                        title="Preview voice"
                        style={{
                          width: 32, height: 32,
                          background: previewingKey === `voice:${selectedVoiceId}` ? T.warning : T.faint,
                          color: previewingKey === `voice:${selectedVoiceId}` ? "#000" : T.text,
                        }}
                      >
                        {previewingKey === `voice:${selectedVoiceId}` ? <Pause size={13} /> : <Play size={13} style={{ marginLeft: 2 }} />}
                      </button>
                    </div>
                      </>
                    )}
                    {/* Bring your own recording instead of AI TTS */}
                    <div className="flex items-center gap-2 mt-3 pt-3" style={{ borderTop: `1px dashed ${T.border}` }}>
                      <input
                        type="file"
                        accept="audio/*,.mp3,.wav,.m4a,.aac,.flac,.ogg"
                        className="hidden"
                        ref={voFileInputRef}
                        data-testid="promo-voiceover-upload-input"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadCustomVocal(f); e.target.value = ""; }}
                      />
                      {!customVocal && (
                        <>
                          <button
                            type="button"
                            data-testid="promo-voiceover-record-btn"
                            onClick={() => setShowRecorder(true)}
                            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
                            style={{ background: T.elevated, border: `1px solid #f43f5e88`, color: T.text, fontSize: 11.5 }}
                          >
                            <Mic2 size={11} style={{ color: "#f43f5e" }} />
                            Record now
                          </button>
                          <button
                            type="button"
                            data-testid="promo-voiceover-upload-btn"
                            onClick={() => voFileInputRef.current?.click()}
                            disabled={uploadingVocal}
                            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
                            style={{ background: T.elevated, border: `1px dashed ${T.purple}88`, color: T.text, fontSize: 11.5, opacity: uploadingVocal ? 0.6 : 1 }}
                          >
                            {uploadingVocal ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                            {uploadingVocal ? "Uploading…" : "Upload a file"}
                          </button>
                        </>
                      )}
                      <span style={{ fontSize: 10, color: T.faint }}>
                        {customVocal ? "Your recording replaces the AI voice in the mix." : "Replaces the AI voice"}
                      </span>
                    </div>
                    {showRecorder && (
                      <VoiceRecorderModal
                        title="Record your voice-over"
                        onCancel={() => setShowRecorder(false)}
                        onUse={(file) => { setShowRecorder(false); uploadCustomVocal(file); }}
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Instrumental mode still needs a name */}
            {mode === "instrumental" && (
              <div className="mb-4">
                <FieldLabel>Promo Name</FieldLabel>
                <input
                  type="text"
                  data-testid="promo-instrumental-name-input"
                  placeholder="Sting name"
                  value={promoName}
                  onChange={(e) => setPromoName(e.target.value)}
                  className="w-full rounded-lg px-3 py-2"
                  style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.text, fontSize: 13 }}
                />
              </div>
            )}

            {/* Render / preview / download */}
            {!renderedPromo ? (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="promo-render"
                  onClick={renderPromo}
                  disabled={rendering}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-2"
                  style={{
                    background: `linear-gradient(135deg,${T.primary} 0%,${T.purple} 100%)`,
                    color: "#fff", fontSize: 13, fontWeight: 600,
                    opacity: rendering ? 0.6 : 1,
                  }}
                >
                  {rendering ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                  {rendering ? "Rendering…" : "Render promo"}
                </button>
                <button
                  type="button"
                  data-testid="promo-step-back"
                  onClick={() => setStep(mode === "instrumental" ? 1 : 2)}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-2"
                  style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.muted, fontSize: 13 }}
                >
                  <ChevronLeft size={14} />
                  Back
                </button>
              </div>
            ) : (
              <div className="rounded-lg p-4" style={{ background: `${T.success}0a`, border: `1px solid ${T.success}55` }}>
                <div className="flex items-center gap-2 mb-3">
                  <Check size={16} style={{ color: T.success }} />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Rendered · {(renderedPromo.size_bytes / 1024).toFixed(1)} KB</span>
                </div>
                {audioUrl && (
                  <audio controls src={audioUrl} className="w-full mb-3" data-testid="promo-audio-player" />
                )}
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    data-testid="promo-download-export"
                    onClick={downloadExport}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
                    style={{
                      background: `linear-gradient(135deg,${T.primary} 0%,${T.purple} 100%)`,
                      color: "#fff", fontSize: 12, fontWeight: 500,
                    }}
                  >
                    <Download size={12} />
                    Download promo + manifest (.zip)
                  </button>
                  <button
                    type="button"
                    data-testid="promo-open-production-suite"
                    onClick={() => navigate("/dashboard/production-suite")}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
                    style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.muted, fontSize: 12 }}
                  >
                    Fine-tune in Production Suite
                    <ArrowRight size={12} />
                  </button>
                  <button
                    type="button"
                    data-testid="promo-send-to-sequencer"
                    onClick={sendToSequencer}
                    disabled={seqSending || seqSent}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
                    style={{
                      background: seqSent ? `${T.success}22` : T.elevated,
                      border: `1px solid ${seqSent ? T.success : T.border}`,
                      color: seqSent ? T.success : T.muted, fontSize: 12,
                      opacity: seqSending ? 0.6 : 1,
                    }}
                  >
                    {seqSending ? <Loader2 size={12} className="animate-spin" /> : seqSent ? <Check size={12} /> : <Music2 size={12} />}
                    {seqSent ? "In your Imaging library" : "Send to Sequencer"}
                  </button>
                  <button
                    type="button"
                    data-testid="promo-start-over"
                    onClick={startOver}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 ml-auto"
                    style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.muted, fontSize: 12 }}
                  >
                    <Sparkles size={12} />
                    Create another
                  </button>
                </div>
              </div>
            )}
          </StepCard>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────
function StepCard({ title, subtitle, children }) {
  return (
    <div className="rounded-2xl p-6" style={{ background: T.card, border: `1px solid ${T.border}` }}>
      <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>{title}</h2>
      {subtitle && <p style={{ color: T.muted, fontSize: 12, marginBottom: 20 }}>{subtitle}</p>}
      {children}
    </div>
  );
}

function StepFooter({ onBack, onNext, nextLabel, nextDisabled, testid }) {
  return (
    <div className="flex items-center justify-between mt-6">
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2"
          style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.muted, fontSize: 12 }}
        >
          <ChevronLeft size={12} />
          Back
        </button>
      ) : <div />}
      <button
        type="button"
        data-testid={testid}
        onClick={onNext}
        disabled={nextDisabled}
        className="inline-flex items-center gap-1.5 rounded-full px-4 py-2"
        style={{
          background: `linear-gradient(135deg,${T.primary} 0%,${T.purple} 100%)`,
          color: "#fff", fontSize: 12, fontWeight: 500,
          opacity: nextDisabled ? 0.5 : 1,
        }}
      >
        {nextLabel}
      </button>
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <div style={{ fontSize: 10, color: T.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
      {children}
    </div>
  );
}

function TextInput({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <FieldLabel>{label}</FieldLabel>
      <input
        type="text"
        data-testid={testid}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded px-2 py-1.5"
        style={{ background: T.card, border: `1px solid ${T.border}`, color: T.text, fontSize: 12 }}
      />
    </label>
  );
}

function NumberInput({ label, value, onChange, min, max, testid }) {
  return (
    <label className="block">
      <FieldLabel>{label}</FieldLabel>
      <input
        type="number"
        data-testid={testid}
        value={value}
        min={min} max={max}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full rounded px-2 py-1.5"
        style={{ background: T.card, border: `1px solid ${T.border}`, color: T.text, fontSize: 12 }}
      />
    </label>
  );
}

function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div className="text-center py-10 rounded-lg" style={{ background: T.elevated, border: `1px dashed ${T.border}` }}>
      <Icon size={22} style={{ color: T.muted, margin: "0 auto 8px" }} />
      <div style={{ fontSize: 13, color: T.text, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 11, color: T.muted }}>{subtitle}</div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-baseline gap-3 py-1">
      <span style={{ fontSize: 10, color: T.muted, textTransform: "uppercase", letterSpacing: 0.5, minWidth: 60 }}>{label}</span>
      <span style={{ fontSize: 13, color: T.text }} className="flex-1">{value}</span>
    </div>
  );
}

function MixBar({ label, value, color, onChange, testid, disabled }) {
  const pct = Math.round(value * 100);
  return (
    <div className="flex items-center gap-3 py-1.5" style={{ opacity: disabled ? 0.4 : 1 }}>
      <span style={{ fontSize: 11, color: T.muted, minWidth: 40 }}>{label}</span>
      <div className="flex-1 rounded-full h-2" style={{ background: T.faint }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <input
        type="range"
        data-testid={testid}
        value={value}
        min={0} max={1.5} step={0.05}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className="cursor-pointer"
        style={{ width: 80, accentColor: color }}
      />
      <span style={{ fontSize: 10, color: T.muted, minWidth: 32, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{pct}%</span>
    </div>
  );
}

// ── PromoDraftRow: one row in the "Resume a draft" panel ────────────
function PromoDraftRow({ draft, onResume, onDelete }) {
  const md = draft.metadata || {};
  const stepLabel = ({ 1: "Bed", 2: "Vocal", 3: "Levels", 4: "Preview" })[md.step] || "Draft";
  const modeLabel = draft.mode === "instrumental" ? "Instrumental" : "With Vocals";
  const ago       = formatAgo(draft.updated_at);
  const hasCustomVocal = !!md.customVocal;
  return (
    <div
      className="w-full rounded-lg px-3 py-2 flex items-center gap-3 transition group cursor-pointer"
      style={{
        background: T.elevated,
        border: `1px solid ${T.border}`,
      }}
      onClick={onResume}
      data-testid={`promo-draft-row-${draft.draft_id}`}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onResume(); }}}
    >
      <div className="rounded"
           style={{ background: `${T.warning}22`, padding: 6, flexShrink: 0 }}>
        <FileText size={12} style={{ color: T.warning }} />
      </div>
      <div className="flex-1 min-w-0">
        <div style={{ fontSize: 13, fontWeight: 500, color: T.text }} className="truncate">
          {draft.title || "Untitled draft"}
        </div>
        <div className="flex items-center gap-2" style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>
          <span>{stepLabel}</span>
          <span style={{ color: T.faint }}>·</span>
          <span>{modeLabel}</span>
          {hasCustomVocal && (
            <>
              <span style={{ color: T.faint }}>·</span>
              <span style={{ color: T.primary }}>custom vocal</span>
            </>
          )}
          <span style={{ color: T.faint }}>·</span>
          <span className="inline-flex items-center gap-1">
            <Clock size={9} /> {ago}
          </span>
        </div>
      </div>
      <button
        type="button"
        data-testid={`promo-draft-resume-${draft.draft_id}`}
        onClick={(e) => { e.stopPropagation(); onResume(); }}
        className="rounded-full inline-flex items-center gap-1 px-2.5 py-1 flex-shrink-0"
        style={{ background: T.purple, color: "#fff", fontSize: 11, fontWeight: 600 }}
      >
        Resume <ChevronRight size={11} />
      </button>
      <button
        type="button"
        data-testid={`promo-draft-delete-${draft.draft_id}`}
        onClick={onDelete}
        className="rounded-full inline-flex items-center justify-center flex-shrink-0 opacity-40 group-hover:opacity-100 transition-opacity"
        title="Delete draft"
        style={{
          width: 26, height: 26,
          background: T.elevated, border: `1px solid ${T.border}`, color: T.danger,
        }}
      >
        <Trash2 size={11} />
      </button>
    </div>
  );
}

// Human-friendly relative timestamp ("2m ago", "3h ago", etc.).
function formatAgo(iso) {
  if (!iso) return "just now";
  try {
    const then = new Date(iso).getTime();
    const secs = Math.max(1, Math.round((Date.now() - then) / 1000));
    if (secs < 60)        return `${secs}s ago`;
    if (secs < 3600)      return `${Math.round(secs / 60)}m ago`;
    if (secs < 86400)     return `${Math.round(secs / 3600)}h ago`;
    if (secs < 604800)    return `${Math.round(secs / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return "recently";
  }
}

